import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Loader2,
  ListTodo,
  ArrowLeft,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { bookingService, type AppointmentType } from '@/services/bookingService';
import {
  intakeFormTemplateService,
  type IntakeFormTemplate,
} from '@/services/intakeFormTemplateService';
import { AppointmentTypeCard } from '@/components/booking';

const INTAKE_NONE = '__none__';

const SYSTEM_THERAPY_OPTIONS: { value: string; label: string }[] = [
  { value: 'sys:trt', label: 'TRT (Men’s Health) — system' },
  { value: 'sys:hrt', label: 'HRT (Women’s Health) — system' },
  { value: 'sys:glp1', label: 'GLP-1 (Weight Management) — system' },
  { value: 'sys:peptide', label: 'Peptide — system' },
  { value: 'sys:nad', label: 'NAD+ — system' },
  { value: 'sys:fertility', label: 'Fertility — system' },
  { value: 'sys:general', label: 'General — system' },
];

// Color options for appointment types
const COLOR_OPTIONS = [
  { value: '#0D9488', label: 'Teal' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#F97316', label: 'Orange' },
  { value: '#22C55E', label: 'Green' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#6B7280', label: 'Gray' },
];

// Duration options
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

interface AppointmentTypeFormData {
  name: string;
  description: string;
  duration_minutes: number;
  price_cents: number | null;
  color: string;
  telehealth_enabled: boolean;
  in_person_enabled: boolean;
  is_public: boolean;
  default_intake_form_template_id: string | null;
  default_therapy_category: string | null;
}

const defaultFormData: AppointmentTypeFormData = {
  name: '',
  description: '',
  duration_minutes: 30,
  price_cents: null,
  color: '#0D9488',
  telehealth_enabled: true,
  in_person_enabled: false,
  is_public: true,
  default_intake_form_template_id: null,
  default_therapy_category: null,
};

const AppointmentTypesPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clinic } = useClinic();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [intakeTemplates, setIntakeTemplates] = useState<IntakeFormTemplate[]>([]);
  // V2 (identity-first / OTP-verified) per-appointment-type booking URLs,
  // keyed by appointment type id. Empty until the v2-links request returns.
  const [v2UrlByType, setV2UrlByType] = useState<Record<string, string>>({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AppointmentTypeFormData>(defaultFormData);

  // Load data
  const loadData = useCallback(async () => {
    if (!clinic?.id) return;

    setLoading(true);
    try {
      const [typesData, v2Links, templates] = await Promise.all([
        bookingService.getAppointmentTypes(clinic.id),
        bookingService.getV2BookingLinks(clinic.id),
        intakeFormTemplateService.list(clinic.id, { active: true, published: true }).catch(() => []),
      ]);

      setAppointmentTypes(typesData);
      setIntakeTemplates(templates);
      const map: Record<string, string> = {};
      v2Links?.appointment_types?.forEach((t) => {
        map[t.id] = t.practitioner_url || t.url;
      });
      setV2UrlByType(map);
    } catch (error) {
      console.error('Failed to load appointment types:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointment types',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [clinic?.id, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open create dialog
  const handleCreate = () => {
    setEditingId(null);
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleEdit = (type: AppointmentType) => {
    setEditingId(type.id);
    setFormData({
      name: type.name,
      description: type.description || '',
      duration_minutes: type.duration_minutes,
      price_cents: type.price_cents || null,
      color: type.color || '#0D9488',
      telehealth_enabled: type.telehealth_enabled,
      in_person_enabled: type.in_person_enabled,
      is_public: true, // Assuming all types are public by default
      default_intake_form_template_id: (type as any).default_intake_form_template_id || null,
      default_therapy_category: (type as any).default_therapy_category || null,
    });
    setDialogOpen(true);
  };

  // Save appointment type
  const handleSave = async () => {
    // Debug logging
    console.log('handleSave called - Clinic:', clinic);
    console.log('handleSave called - FormData:', formData);
    
    // Validate clinic
    if (!clinic?.id) {
      console.error('No clinic ID available');
      toast({
        title: 'Error',
        description: 'Clinic information is not available. Please refresh the page.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validate form
    if (!formData.name) {
      toast({
        title: 'Validation Error',
        description: 'Please enter an appointment type name.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await bookingService.updateAppointmentType(clinic.id, editingId, formData);
        toast({
          title: 'Success',
          description: 'Appointment type updated',
        });
      } else {
        await bookingService.createAppointmentType(clinic.id, formData);
        toast({
          title: 'Success',
          description: 'Appointment type created',
        });
      }

      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to save appointment type',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (type: AppointmentType, active: boolean) => {
    if (!clinic?.id) return;

    try {
      if (!active) {
        await bookingService.deleteAppointmentType(clinic.id, type.id);
        toast({
          title: 'Success',
          description: 'Appointment type disabled',
        });
      }
      loadData();
    } catch (error) {
      console.error('Failed to toggle appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update appointment type',
        variant: 'destructive',
      });
    }
  };

  // Copy the V2 (identity-first / OTP-verified) booking link for a single
  // appointment type. The URL is generated server-side from clinic +
  // appointment-type slugs, so each type has a stable, shareable URL.
  const handleCopyLink = (typeId: string) => {
    const url = v2UrlByType[typeId];
    if (!url) {
      toast({
        title: 'Link not ready',
        description: 'This appointment type does not have a booking link yet. Reload the page in a moment.',
        variant: 'destructive',
      });
      return;
    }
    navigator.clipboard.writeText(url);
    toast({
      title: 'Copied!',
      description: 'Booking link copied to clipboard',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header with Back Navigation */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-9 w-9"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Appointment Types</h1>
              <p className="text-slate-500 mt-1">
                Define the types of appointments patients can book
              </p>
            </div>
          </div>
          <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-2" />
            New Appointment Type
          </Button>
        </div>

        {/* Appointment Type Cards */}
        {appointmentTypes.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="text-center text-slate-500">
                <ListTodo className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  No appointment types yet
                </h3>
                <p className="mb-6">Create your first appointment type to start accepting bookings</p>
                <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Appointment Type
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {appointmentTypes.map((type) => (
              <AppointmentTypeCard
                key={type.id}
                id={type.id}
                name={type.name}
                description={type.description}
                durationMinutes={type.duration_minutes}
                price={type.price}
                color={type.color}
                telehealthEnabled={type.telehealth_enabled}
                inPersonEnabled={type.in_person_enabled}
                isActive={true}
                isPublic={true}
                onEdit={() => handleEdit(type)}
                onToggleActive={(active) => handleToggleActive(type, active)}
                onCopyLink={() => handleCopyLink(type.id)}
              />
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Edit Appointment Type' : 'New Appointment Type'}
              </DialogTitle>
              <DialogDescription>
                Configure the details for this appointment type
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Initial Consultation"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this appointment type..."
                  rows={2}
                />
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={formData.duration_minutes.toString()}
                  onValueChange={(v) => setFormData({ ...formData, duration_minutes: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value.toString()}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Visit Fee */}
              <div className="space-y-2">
                <Label htmlFor="price">Visit Fee ($)</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price_cents != null ? (formData.price_cents / 100).toFixed(2) : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      price_cents: val === '' ? null : Math.round(parseFloat(val) * 100),
                    });
                  }}
                  placeholder="0.00"
                />
                <p className="text-xs text-slate-500">Leave empty for free visits</p>
              </div>

              {/* Color */}
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === color.value
                          ? 'border-slate-900 scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              {/* Meeting Type */}
              <div className="space-y-3">
                <Label>Meeting Type</Label>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Video Call</span>
                    <Switch
                      checked={formData.telehealth_enabled}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, telehealth_enabled: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">In-Person</span>
                    <Switch
                      checked={formData.in_person_enabled}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, in_person_enabled: checked })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Default intake form */}
              <div className="space-y-2">
                <Label>Default pre-visit intake</Label>
                <p className="text-xs text-slate-500">
                  Auto-sent to patients when this appointment type is booked
                  (both staff-booked and public booking link).
                </p>
                <Select
                  value={
                    formData.default_intake_form_template_id
                      ? `tpl:${formData.default_intake_form_template_id}`
                      : formData.default_therapy_category
                        ? `sys:${formData.default_therapy_category}`
                        : INTAKE_NONE
                  }
                  onValueChange={(v) => {
                    if (v === INTAKE_NONE) {
                      setFormData({
                        ...formData,
                        default_intake_form_template_id: null,
                        default_therapy_category: null,
                      });
                    } else if (v.startsWith('tpl:')) {
                      setFormData({
                        ...formData,
                        default_intake_form_template_id: v.slice(4),
                        default_therapy_category: null,
                      });
                    } else if (v.startsWith('sys:')) {
                      setFormData({
                        ...formData,
                        default_intake_form_template_id: null,
                        default_therapy_category: v.slice(4),
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No intake by default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INTAKE_NONE}>No intake by default</SelectItem>
                    {intakeTemplates.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs uppercase tracking-wide text-slate-400">
                          Custom
                        </div>
                        {intakeTemplates.map(t => (
                          <SelectItem key={t.id} value={`tpl:${t.id}`}>{t.name}</SelectItem>
                        ))}
                      </>
                    )}
                    <div className="px-2 py-1 text-xs uppercase tracking-wide text-slate-400 mt-1">
                      System
                    </div>
                    {SYSTEM_THERAPY_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Public Toggle */}
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label>Public</Label>
                  <p className="text-sm text-slate-500">Allow patients to book this type</p>
                </div>
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_public: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AppointmentTypesPage;






